import { PROFILE_LABEL_SX } from "../components/profileFieldStyles";
import { openContactEmail, openContactCall } from "../utils/contactCommunication";
import CompanyTableCell from "../components/CompanyTableCell";
import LeadActivitiesTabContent from "../components/LeadActivitiesTabContent";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import {
  Calendar,
  Call,
  DocumentText,
  MagicStar,
  Send2,
  Sms,
  UserEdit,
} from "iconsax-react";
import Breadcrumb from "../components/Breadcrumb";
import StatsSlider from "../components/StatsSlider";
import CompanyProfileHeader from "../components/CompanyProfileHeader";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import QuickActionsCard from "../components/sidebar-widgets/QuickActionsCard";
import { openInterviewScheduler } from "../utils/interviewScheduling";
import ClientPortalAccess from "../components/ClientPortalAccess";
import AIAgentCard from "../components/sidebar-widgets/AIAgentCard";
import GenericDataTable from "../components/GenericDataTable";
import ContactDocuments from "../components/ContactDocuments";
import JobNotesCard from "../components/sidebar-widgets/JobNotesCard";
import {
  TAB_VIEW_CONTENT_WRAPPER_SX,
  TAB_VIEW_HEADER_SX,
} from "../components/tabViewContentSpacing";
import {
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "../components/sidebar-widgets/sidebarWidgetStyles";
import InfoActionIcons from "../components/InfoActionIcons";
import LocationSearchableField from "../components/LocationSearchableField";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import { normalizeContactActionHref } from "../utils/contactChannels";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import { buildHiringManagerSequenceItem } from "../utils/sequenceItems";
import { fetchTeamMembers } from "../utils/teamMembers";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const text = (value, fallback = "") => String(value ?? "").trim() || fallback;
const initials = (name) =>
  text(name, "?")
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
const authHeaders = () => {
  const storedToken =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    "";
  if (!storedToken) return {};

  const token = String(storedToken).replace(/^Bearer\s+/i, "").trim();
  return token ? { Authorization: `Bearer ${token}`, "x-access-token": token } : {};
};
const CONTACT_TABS = [
  { value: "details", label: "Details" },
  { value: "leads", label: "Leads" },
  { value: "jobs", label: "Jobs" },
  { value: "candidates", label: "Candidates" },
  { value: "sequences", label: "Sequences" },
  { value: "proposals", label: "Proposals" },
  { value: "activities", label: "Activities" },
  { value: "notes", label: "Notes" },
  { value: "files", label: "Files" },
];
const TAB_COPY = {
  leads: ["Leads", ["Lead", "Status", "Last Activity"]],
  jobs: [
    "Jobs",
    ["Job Title", "Company", "Status", "Candidates", "Last Activity"],
  ],
  candidates: [
    "Candidates",
    ["Candidate", "Job", "Stage", "Feedback", "Last Activity"],
  ],
  sequences: [
    "Sequences",
    ["Sequence Name", "Status", "Sent", "Opened", "Replied", "Last Step"],
  ],
  proposals: [
    "Proposals",
    ["Proposal Name", "Status", "Value", "Sent Date", "Viewed", "Signed"],
  ],
  activities: ["Activities", ["Activity", "Type", "Date", "Owner"]],
  notes: ["Notes", ["Note", "Created", "Owner"]],
  files: ["Files", ["File Name", "Type", "Uploaded", "Owner"]],
};

function SidebarCard({ title, children }) {
  return (
    <Box className="widget" sx={{ ...SIDEBAR_WIDGET_CARD_SX, p: 2.5 }}>
      <Typography data-popup-section
        className="heading"
        sx={{
          ...SIDEBAR_WIDGET_TITLE_SX,
          fontSize: 18,
          fontWeight: 700,
          color: "#3451F4",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ mt: 2 }}>{children}</Box>
    </Box>
  );
}
function SidebarActionLink({ children, onClick }) {
  return (
    <Button
      size="small"
      onClick={onClick}
      sx={{
        minWidth: 0,
        px: 0,
        py: 0,
        color: "#9C4FFF",
        fontSize: 12,
        fontWeight: 500,
        textTransform: "none",
        "&:hover": { backgroundColor: "transparent", color: "#7C3AED" },
      }}
    >
      {children}
    </Button>
  );
}
function SectionHeader({ title, onEdit, editing, onSave, saving, action }) {
  const isActivitiesHeading = title === "Activities";
  const actionSx = {
    minWidth: 0,
    px: 0,
    py: 0,
    color: "#9C4FFF",
    fontSize: 12,
    fontWeight: 500,
    textTransform: "none",
    lineHeight: 1.2,
    "&:hover": { backgroundColor: "transparent", color: "#7C3AED" },
  };
  return (
    <Box
      sx={{
        borderTop: "1px solid #8ED0FF",
        borderBottom: "1px solid #8ED0FF",
        py: 1.65,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
      }}
    >
      <Typography
        sx={{
          color: "#3451F4",
          fontSize: isActivitiesHeading ? 16 : 18,
          fontWeight: isActivitiesHeading ? 700 : 800,
          lineHeight: isActivitiesHeading ? "24px" : undefined,
        }}
      >
        {title}
      </Typography>
      {action || (onEdit ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
        {editing ? (
            <Button
              variant="text"
              size="small"
              sx={actionSx}
                onClick={onSave}
                disabled={saving}
              >
              {saving ? <CircularProgress size={14} /> : "Save"}
            </Button>
        ) : (
            <Button variant="text" size="small" sx={actionSx} onClick={onEdit}>
              Edit
            </Button>
          )}
        </Stack>
      ) : null)}
    </Box>
  );
}

export default function Contact() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("details");
  const [contact, setContact] = useState(() => location.state?.contact || {});
  const [contactStatus, setContactStatus] = useState(
    () => location.state?.contact?.status || "New",
  );
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [contactActivities, setContactActivities] = useState([]);

  const [activitiesRefreshKey, setActivitiesRefreshKey] = useState(0);
  useEffect(() => {
    const refresh = () => setActivitiesRefreshKey(value => value + 1);
    window.addEventListener("rpo:activities-refresh", refresh);
    return () => window.removeEventListener("rpo:activities-refresh", refresh);
  }, []);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [relatedLeads, setRelatedLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [relatedCandidates, setRelatedCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [relatedSequences, setRelatedSequences] = useState([]);
  const [sequencesLoading, setSequencesLoading] = useState(false);
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [contactFiles, setContactFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [deletingFiles, setDeletingFiles] = useState(false);
  const [fileError, setFileError] = useState("");
  const [statusFeedback, setStatusFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [form, setForm] = useState({});
  const [ownerOptions, setOwnerOptions] = useState([]);
  const profileImageInputRef = useRef(null);
  const linkedinImageResolutionIds = useRef(new Set());
  useEffect(() => {
    if (location.state?.contact) {
      setContact(location.state.contact);
      setContactStatus(location.state.contact.status || "New");
    }
  }, [location.key, location.state]);
  useEffect(() => {
    if (editing) return;
    setForm({
      name: contact.name || "",
      role: contact.role || contact.title || "",
      email: contact.email || "",
      phone: contact.phone || "",
      linkedin_url: contact.linkedin_url || "",
      source: contact.source || "",
      location: contact.location || "",
      owner: contact.owner || "",
      profile_image: contact.profile_image || "",
      contact_kind: contact.contact_kind || "contact",
    });
  }, [contact, editing]);
  useEffect(() => {
    if (contact.status) setContactStatus(contact.status);
  }, [contact.status]);
  useEffect(() => {
    let active = true;
    fetchTeamMembers({ apiBase: API_BASE })
      .then((members) => {
        if (active) setOwnerOptions(members.map((member) => member.name).filter(Boolean));
      })
      .catch(() => {
        if (active) setOwnerOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    fetch(`${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}`, {
      headers: authHeaders(),
      credentials: "include",
    })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const next = payload?.hiringManager || payload?.contact || payload;
        if (active && next && typeof next === "object")
          setContact((previous) => ({ ...previous, ...next }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [id, activitiesRefreshKey]);
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    fetch(`${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}/files`, {
      headers: authHeaders(),
      credentials: "include",
    })
      .then(async (response) => (response.ok ? response.json() : { files: [] }))
      .then((payload) => {
        if (active) {
          setContactFiles(Array.isArray(payload?.files) ? payload.files : []);
          setSelectedFileIds([]);
        }
      })
      .catch(() => {
        if (active) setContactFiles([]);
      });
    return () => {
      active = false;
    };
  }, [id, activitiesRefreshKey]);
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    setActivitiesLoading(true);
    fetch(
      `${API_BASE}/api/activities?entityType=contact&entityId=${encodeURIComponent(id)}`,
      { headers: authHeaders(), credentials: "include" },
    )
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (active)
          setContactActivities(
            Array.isArray(payload?.activities) ? payload.activities : [],
          );
      })
      .catch(() => {
        if (active) setContactActivities([]);
      })
      .finally(() => {
        if (active) setActivitiesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activitiesRefreshKey, contact.updated_at, id]);
  useEffect(() => {
    const relatedCompanyName = text(
      contact.entity_name || contact.company_name,
      "",
    );
    if (!relatedCompanyName) {
      setRelatedLeads([]);
      return undefined;
    }
    let active = true;
    setLeadsLoading(true);
    fetch(
      `${API_BASE}/api/leads?search=${encodeURIComponent(relatedCompanyName)}&limit=100`,
      { headers: authHeaders(), credentials: "include" },
    )
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!active) return;
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        const normalizedCompany = relatedCompanyName.toLowerCase();
        setRelatedLeads(
          rows.filter(
            (row) =>
              String(row?.company_name || "")
                .trim()
                .toLowerCase() === normalizedCompany,
          ),
        );
      })
      .catch(() => {
        if (active) setRelatedLeads([]);
      })
      .finally(() => {
        if (active) setLeadsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [contact.entity_name, contact.company_name, activitiesRefreshKey]);
  useEffect(() => {
    const companyId = Number(contact.entity_id || 0);
    if (!Number.isFinite(companyId) || companyId <= 0) {
      setRelatedJobs([]);
      return undefined;
    }
    let active = true;
    setJobsLoading(true);
    fetch(
      `${API_BASE}/api/jobs?companyId=${encodeURIComponent(companyId)}&limit=100`,
      { headers: authHeaders(), credentials: "include" },
    )
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (active)
          setRelatedJobs(Array.isArray(payload?.rows) ? payload.rows : []);
      })
      .catch(() => {
        if (active) setRelatedJobs([]);
      })
      .finally(() => {
        if (active) setJobsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [contact.entity_id, activitiesRefreshKey]);
  useEffect(() => {
    if (!relatedLeads.length) {
      setRelatedCandidates([]);
      return undefined;
    }
    let active = true;
    setCandidatesLoading(true);
    Promise.all(
      relatedLeads.map((lead) =>
        fetch(
          `${API_BASE}/api/candidates?leadId=${encodeURIComponent(lead.id)}&limit=100`,
          { headers: authHeaders(), credentials: "include" },
        )
          .then(async (response) => (response.ok ? response.json() : null))
          .then((payload) =>
            Array.isArray(payload?.rows)
              ? payload.rows.map((candidate) => ({
                  ...candidate,
                  related_job_title: lead.job_title || "-",
                }))
              : [],
          ),
      ),
    )
      .then((results) => {
        if (!active) return;
        const seen = new Set();
        setRelatedCandidates(
          results.flat().filter((candidate) => {
            const key = String(candidate.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }),
        );
      })
      .catch(() => {
        if (active) setRelatedCandidates([]);
      })
      .finally(() => {
        if (active) setCandidatesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [relatedLeads]);
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    setSequencesLoading(true);
    fetch(`${API_BASE}/api/sequences?entityType=contact&entityId=${encodeURIComponent(id)}`, {
      headers: authHeaders(),
      credentials: "include",
    })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (active)
          setRelatedSequences(Array.isArray(payload?.rows) ? payload.rows : []);
      })
      .catch(() => {
        if (active) setRelatedSequences([]);
      })
      .finally(() => {
        if (active) setSequencesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activitiesRefreshKey, id]);
  const companyName = text(
    contact.entity_name || contact.company_name || location.state?.companyName,
    "Company",
  );
  const name = text(contact.name || contact.contact_name, "Contact");
  const role = text(contact.role || contact.title, "Contact");
  const resolveLinkedInProfileImage = useCallback(async () => {
    const contactId = String(id || "").trim();
    if (
      !contactId ||
      !String(contact.linkedin_url || "").trim() ||
      linkedinImageResolutionIds.current.has(contactId)
    ) {
      return;
    }

    linkedinImageResolutionIds.current.add(contactId);
    // Stop retrying the broken remote image while the replacement is resolved.
    setContact((previous) => ({ ...previous, profile_image: "" }));
    try {
      const response = await fetch(
        `${API_BASE}/api/hiring-managers/${encodeURIComponent(contactId)}/profile-image/resolve-linkedin`,
        {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
        },
      );
      const payload = await response.json().catch(() => ({}));
      const hiringManager = payload?.hiringManager;
      if (!response.ok || !hiringManager?.profile_image) return;

      setContact((previous) => ({ ...previous, ...hiringManager }));
      setForm((previous) => ({ ...previous, profile_image: hiringManager.profile_image }));
    } catch {
      // Keep the initials fallback when LinkedIn does not expose a public photo.
    }
  }, [contact.linkedin_url, id]);
  useEffect(() => {
    if (!contact.profile_image && contact.linkedin_url) {
      void resolveLinkedInProfileImage();
    }
  }, [contact.linkedin_url, contact.profile_image, resolveLinkedInProfileImage]);
  const actions = useMemo(
    () =>
      [
        { type: "email", href: contact.email },
        { type: "phone", href: contact.phone },
        { type: "linkedin", href: contact.linkedin_url },
      ].filter((item) => normalizeContactActionHref(item.type, item.href)),
    [contact],
  );
  const counts = {
    // These tabs render the related datasets below, so their badges and summary
    // cards must use those same rows instead of potentially stale profile totals.
    leads: relatedLeads.length,
    jobs: relatedJobs.length,
    candidates: Number(contact.candidates_count || 0),
    sequences: Number(contact.sequences_count || 0),
    proposals: Number(contact.proposals_count || 0),
    activities: Number(contactActivities.length || contact.activities_count || 0),
    notes:
      (Array.isArray(contact.notes) ? contact.notes.length : 0) +
      contactActivities.filter(
        (activity) => String(activity?.activity_type || activity?.type || "").toLowerCase() === "note"
      ).length,
    files: contactFiles.length,
  };
  const save = async () => {
    if (!id || !form.name.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      const response = await fetch(
        `${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({ ...form, status: contactStatus }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not update contact.");
      const savedContact = payload?.hiringManager || {};
      const nextContact = {
        ...form,
        ...savedContact,
        role: savedContact.role || savedContact.title || form.role,
        status: savedContact.status || contactStatus,
      };
      setContact((previous) => ({
        ...previous,
        ...nextContact,
      }));
      setForm(nextContact);
      if (payload?.activity) {
        setContactActivities((previous) => [
          payload.activity,
          ...previous.filter((activity) => activity.id !== payload.activity.id),
        ]);
      }
      setEditing(false);
      setSaveError("Contact information saved.");
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };
  const uploadProfileImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id) return;
    if (!file.type.startsWith("image/")) {
      setSaveError("Please choose an image file.");
      return;
    }
    setUploadingImage(true);
    setSaveError("");
    try {
      const data = new FormData();
      data.append("profile_image", file);
      const response = await fetch(
        `${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}/profile-image`,
        {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: data,
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not upload profile image.");
      const profileImage = payload?.hiringManager?.profile_image || "";
      setForm((previous) => ({ ...previous, profile_image: profileImage }));
      setContact((previous) => ({
        ...previous,
        ...(payload?.hiringManager || {}),
        profile_image: profileImage,
      }));
      setSaveError("Profile image uploaded.");
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setUploadingImage(false);
    }
  };
  const uploadContactFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id || uploadingFile) return;

    const fileType = String(file.type || "").toLowerCase();
    const fileName = String(file.name || "").toLowerCase();
    if (fileType !== "application/pdf" && !fileName.endsWith(".pdf")) {
      setFileError("Only PDF files are allowed.");
      return;
    }

    setUploadingFile(true);
    setFileError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}/files`,
        {
          method: "POST",
          headers: authHeaders(),
          credentials: "include",
          body: formData,
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Could not upload file.");
      if (payload?.file) setContactFiles((previous) => [payload.file, ...previous]);
      if (payload?.activity) setContactActivities((previous) => [payload.activity, ...previous]);
    } catch (error) {
      setFileError(error?.message || "Could not upload file.");
    } finally {
      setUploadingFile(false);
    }
  };
  const deleteSelectedFiles = async () => {
    const selectedFiles = contactFiles.filter((file) => selectedFileIds.includes(String(file.id)));
    if (!id || !selectedFiles.length || deletingFiles) return;

    setDeletingFiles(true);
    setFileError("");
    try {
      const response = await fetch(`${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}/files`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({
          files: selectedFiles.map((file) => ({
            activity_id: file.activity_id,
            attachment_index: file.attachment_index,
          })),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Could not delete selected files.");
      const idSet = new Set(selectedFileIds);
      setContactFiles((previous) => previous.filter((file) => !idSet.has(String(file.id))));
      setSelectedFileIds([]);
    } catch (error) {
      setFileError(error?.message || "Could not delete selected files.");
    } finally {
      setDeletingFiles(false);
    }
  };
  const saveStatus = async (nextStatus) => {
    const previousStatus = contactStatus;
    setContactStatus(nextStatus);
    if (!id) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/hiring-managers/${encodeURIComponent(id)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({ ...form, status: nextStatus }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not update status.");
      const savedContact = payload?.hiringManager || {};
      setContact((previous) => ({
        ...previous,
        ...form,
        ...savedContact,
        status: nextStatus,
      }));
      setForm((previous) => ({
        ...previous,
        ...savedContact,
        status: nextStatus,
      }));
      if (payload?.activity) {
        setContactActivities((previous) => [
          payload.activity,
          ...previous.filter((activity) => activity.id !== payload.activity.id),
        ]);
      }
      setStatusFeedback({
        open: true,
        severity: "success",
        message: "Contact status saved.",
      });
    } catch (error) {
      setContactStatus(previousStatus);
      setStatusFeedback({
        open: true,
        severity: "error",
        message: error.message || "Could not update status.",
      });
    }
  };
  const companyEntityType = text(contact.entity_type).toLowerCase();
  const linkedCompanyId =
    companyEntityType === "company" || companyEntityType === "client"
      ? contact.entity_id
      : contact.company_id;
  const companyUrl = linkedCompanyId
    ? `/companies?companyId=${encodeURIComponent(linkedCompanyId)}&tab=details`
    : "/companies";
  const leadColumns = [
    { id: "company_name", label: "Company Name", width: "30%" },
    { id: "job_title", label: "Opportunity", width: "30%" },
    { id: "status", label: "Status", width: "20%" },
    { id: "actions", label: "", width: "20%" },
  ];
  const renderLeadRow = (row) => (
    <>
      <TableCell><CompanyTableCell row={row} name={row.company_name || companyName} /></TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.job_title || "Business opportunity"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.status || "-"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          onClick={() =>
            navigate(`/leads?leadId=${encodeURIComponent(row.id)}&tab=details`)
          }
          sx={{
            color: "#9C4FFF",
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          View details
        </Button>
      </TableCell>
    </>
  );
  const jobColumns = [
    { id: "job_title", label: "Job Title", width: "22%" },
    { id: "company_name", label: "Company Name", width: "16%" },
    { id: "status", label: "Status", width: "12%" },
    { id: "candidates_count", label: "Candidates", width: "12%" },
    { id: "pipeline_interview_count", label: "Interviews", width: "12%" },
    { id: "owner", label: "Owner", width: "14%" },
    { id: "job_list_last_activity_time", label: "Last Activity", width: "12%" },
  ];
  const renderJobRow = (row) => (
    <>
      <TableCell>
        <Button
          size="small"
          onClick={() =>
            navigate(`/jobs?jobId=${encodeURIComponent(row.id)}&tab=details`)
          }
          sx={{
            color: "#475467",
            fontSize: 12.5,
            fontWeight: 600,
            textTransform: "none",
            px: 0,
          }}
        >
          {row.job_title || "Untitled job"}
        </Button>
      </TableCell>
      <TableCell><CompanyTableCell row={row} name={row.linked_company_name || row.company_name || companyName} /></TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.status || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.candidates_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.pipeline_interview_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.owner || row.owner_name || row.recruiter_name || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {formatRelativeTimeShort(row.job_list_last_activity_time)}
        </Typography>
      </TableCell>
    </>
  );
  const candidateColumns = [
    { id: "full_name", label: "Candidate", width: "28%" },
    { id: "related_job_title", label: "Job", width: "24%" },
    { id: "stage", label: "Stage", width: "16%" },
    { id: "feedback", label: "Feedback", width: "18%" },
    { id: "updated_at", label: "Last Activity", width: "14%" },
  ];
  const renderCandidateRow = (row) => (
    <>
      <TableCell>
        <Button
          size="small"
          onClick={() =>
            navigate(
              `/candidates?candidateId=${encodeURIComponent(row.id)}&tab=details`,
            )
          }
          sx={{
            color: "#475467",
            fontSize: 12.5,
            fontWeight: 600,
            textTransform: "none",
            px: 0,
          }}
        >
          {row.full_name || "Candidate"}
        </Button>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.related_job_title || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.stage || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.feedback || row.notes || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {formatRelativeTimeShort(row.last_activity_time || row.last_activity_at || row.updated_at)}
        </Typography>
      </TableCell>
    </>
  );
  const sequenceColumns = [
    { id: "name", label: "Sequence Name", width: "28%" },
    { id: "status", label: "Status", width: "13%" },
    { id: "sent_count", label: "Sent", width: "11%" },
    { id: "opened_count", label: "Opened", width: "12%" },
    { id: "clicked_count", label: "Clicked", width: "12%" },
    { id: "replied_count", label: "Replied", width: "12%" },
    { id: "last_step", label: "Last Step", width: "12%" },
  ];
  const renderSequenceRow = (row) => (
    <>
      <TableCell>
        <Typography sx={{ color: "#475467", fontSize: 12.5, fontWeight: 600 }}>
          {row.name || "Sequence"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.status || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.sent_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.opened_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.clicked_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {Number(row.replied_count || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.last_step || row.step_count || "-"}
        </Typography>
      </TableCell>
    </>
  );
  const proposalRows = Array.isArray(contact.proposals)
    ? contact.proposals
    : [];
  const proposalColumns = [
    { id: "name", label: "Proposal Name", width: "28%" },
    { id: "status", label: "Status", width: "16%" },
    { id: "value", label: "Value", width: "16%" },
    { id: "sent_date", label: "Sent Date", width: "16%" },
    { id: "viewed", label: "Viewed", width: "12%" },
    { id: "signed", label: "Signed", width: "12%" },
  ];
  const renderProposalRow = (row) => (
    <>
      <TableCell>
        <Typography sx={{ color: "#475467", fontSize: 12.5, fontWeight: 600 }}>
          {row.name || row.proposal_name || "Proposal"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.status || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.value || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.sent_date || row.sent_at || "-"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.viewed ? "Yes" : "No"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
          {row.signed ? "Yes" : "No"}
        </Typography>
      </TableCell>
    </>
  );
  const summaryRow = (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ pb: 1.25, minWidth: 0 }}
    >
      <Avatar
        src={contact.profile_image || undefined}
        imgProps={{ onError: resolveLinkedInProfileImage }}
        sx={{ width: 38, height: 38, bgcolor: "#6D5DFB", fontWeight: 700 }}
      >
        {initials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ color: "#475467", fontSize: 14, fontWeight: 600 }}
          noWrap
        >
          {name}
        </Typography>
        <Typography sx={{ color: "#98A2B3", fontSize: 14 }} noWrap>
          {role} · {companyName}
        </Typography>
      </Box>
      <Box sx={{ ml: "auto" }}>
        <InfoActionIcons
          actions={actions}
          showDisabled
          showGroupDivider={false}
          iconSize={19}
        />
      </Box>
    </Stack>
  );
  const statItems = [
    { id: "companies", value: "1", label: "Companies" },
    { id: "leads", value: String(counts.leads), label: "Leads" },
    { id: "jobs", value: String(counts.jobs), label: "Jobs" },
    { id: "candidates", value: String(counts.candidates), label: "Candidates" },
    { id: "sequences", value: String(counts.sequences), label: "Sequences" },
    { id: "proposals", value: String(counts.proposals), label: "Proposals" },
    { id: "activities", value: String(counts.activities), label: "Activities" },
  ];
  const sequenceItem = useMemo(
    () => buildHiringManagerSequenceItem({ ...contact, id }, "contact-profile"),
    [contact, id],
  );
  const quickActionItems = [
    {
      key: "email",
      label: "Email",
      icon: <Sms size="17" color="#fff" />,
      onClick: () => {
        openContactEmail(contact);
      },
    },
    {
      key: "call",
      label: "Call",
      icon: <Call size="17" color="#fff" />,
      onClick: () => {
        openContactCall(contact);
      },
    },
    {
      key: "schedule",
      label: "Schedule",
      icon: <Calendar size="17" color="#fff" />,
      onClick: () => openInterviewScheduler({ contactId: contact.id, contact: { id: contact.id, name: contact.name || contact.full_name, email: contact.email } }),
    },
    {
      key: "sequence",
      label: "Add to Sequence",
      icon: <Send2 size="17" color="#fff" />,
      onClick: () => setIsAddToSequenceOpen(true),
    },
    {
      key: "proposal",
      label: "Create Proposal",
      icon: <DocumentText size="17" color="#fff" />,
      onClick: () => setTab("proposals"),
    },
    {
      key: "assign_owner",
      label: "Assign Owner",
      icon: <UserEdit size="17" color="#fff" />,
      onClick: () => {
        setTab("details");
        setEditing(true);
      },
    },
    {
      key: "launch_agent",
      label: "Launch Agent",
      icon: <MagicStar size="17" color="#fff" />,
      onClick: () =>
        setStatusFeedback({
          open: true,
          severity: "success",
          message: `AI Agent is ready for ${name}.`,
        }),
    },
  ];
  const openLeads = Array.isArray(contact.open_leads) ? contact.open_leads : [];
  const recentActivities = Array.isArray(contact.activities)
    ? contact.activities
    : contactActivities;
  const notesText = text(contact.notes || contact.note, "");
  const latestActivityNote = useMemo(
    () =>
      contactActivities
        .filter(
          (activity) =>
            String(activity?.activity_type || activity?.type || "")
              .trim()
              .toLowerCase() === "note",
        )
        .sort(
          (left, right) =>
            new Date(right?.created_at || right?.createdAt || 0).getTime() -
            new Date(left?.created_at || left?.createdAt || 0).getTime(),
        )[0] || null,
    [contactActivities],
  );
  const sidebarNotesText = text(
    latestActivityNote?.details || latestActivityNote?.summary,
    notesText,
  );
  const contactSidebarWidgets = (
    <>
      <SidebarCard title="Related Company">
        <Typography sx={{ color: "#475467", fontSize: 13, fontWeight: 600 }}>
          {companyName}
        </Typography>
        <Typography sx={{ color: "#98A2B3", fontSize: 12, mt: 0.5 }}>
          Company associated with this contact
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          <SidebarActionLink onClick={() => navigate(companyUrl)}>
            View company details
          </SidebarActionLink>
        </Box>
      </SidebarCard>
      <SidebarCard title="Open Leads">
        {openLeads.length ? (
          <Stack spacing={1}>
            {openLeads.slice(0, 3).map((lead, index) => (
              <Typography
                key={lead.id || index}
                sx={{ color: "#475467", fontSize: 12.5, fontWeight: 600 }}
              >
                {text(lead.name || lead.title, "Lead")}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography sx={{ color: "#667085", fontSize: 12.5 }}>
            {counts.leads
              ? `${counts.leads} active lead${counts.leads === 1 ? "" : "s"} associated with this contact.`
              : "No active leads associated with this contact."}
          </Typography>
        )}
        <Box sx={{ mt: 1.25 }}>
          <SidebarActionLink onClick={() => setTab("leads")}>
            View all leads
          </SidebarActionLink>
        </Box>
      </SidebarCard>
      <SidebarCard title="Recent Activity">
        {recentActivities.length ? (
          <Stack spacing={1}>
            {recentActivities.slice(0, 2).map((activity, index) => (
              <Typography
                key={activity.id || index}
                sx={{ color: "#475467", fontSize: 12.5, lineHeight: 1.5 }}
              >
                {text(
                  activity.title || activity.description,
                  "Contact activity",
                )}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{ color: "#667085", fontSize: 12.5, lineHeight: 1.55 }}
          >
            {contact.updated_at
              ? `Contact updated ${new Date(contact.updated_at).toLocaleDateString()}.`
              : "No recent interactions or system activity."}
          </Typography>
        )}
        <Box sx={{ mt: 1.25 }}>
          <SidebarActionLink onClick={() => setTab("activities")}>
            View all activity
          </SidebarActionLink>
        </Box>
      </SidebarCard>
      <SidebarCard title="Notes">
        <Typography
          sx={{
            color: sidebarNotesText ? "#475467" : "#667085",
            fontSize: 12.5,
            lineHeight: 1.55,
          }}
        >
          {sidebarNotesText || "No internal notes for this contact yet."}
        </Typography>
        <Box sx={{ mt: 1.25 }}>
          <SidebarActionLink onClick={() => setTab("notes")}>
            View all notes
          </SidebarActionLink>
        </Box>
      </SidebarCard>
    </>
  );
  const field = (label, key, readOnly = false) => (
    <Box key={key}>
      <Typography
        className="profile-field-label"
        sx={{ ...PROFILE_LABEL_SX, mb: 2 }}
      >
        {label}
      </Typography>
      {editing && !readOnly ? (
        key === "profile_image" ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              fullWidth
              size="small"
              value={form.profile_image || ""}
              placeholder="https://example.com/profile-image.jpg"
              inputProps={{ "aria-label": "Profile image URL" }}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  profile_image: event.target.value,
                }))
              }
            />
            <input
              ref={profileImageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={uploadProfileImage}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => profileImageInputRef.current?.click()}
              disabled={uploadingImage}
              sx={{
                minWidth: 118,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              {uploadingImage ? "Uploading..." : "Browse files"}
            </Button>
          </Stack>
        ) : key === "location" ? (
          <LocationSearchableField
            value={form.location || ""}
            onChange={(nextLocation) =>
              setForm((previous) => ({ ...previous, location: nextLocation }))
            }
            placeholder="Search city, state, or country"
            textFieldProps={{ fullWidth: true, autoComplete: "off" }}
          />
        ) : key === "owner" ? (
          <Autocomplete
            freeSolo
            options={ownerOptions}
            inputValue={form.owner || ""}
            onInputChange={(_, nextOwner) =>
              setForm((previous) => ({ ...previous, owner: nextOwner || "" }))
            }
            onChange={(_, nextOwner) =>
              setForm((previous) => ({ ...previous, owner: String(nextOwner || "") }))
            }
            slotProps={{
              popper: {
                placement: "bottom-start",
                modifiers: [{ name: "flip", enabled: false }],
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="Search owner"
                autoComplete="off"
              />
            )}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            value={form[key] || ""}
            autoComplete={key === "linkedin_url" ? "off" : undefined}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                [key]: event.target.value,
              }))
            }
          />
        )
      ) : (
        <Box
          data-profile-field
          sx={{
            minHeight: 42,
            border: "1px solid #D2D8DE",
            borderRadius: "8px",
            px: 2,
            display: "flex",
            alignItems: "center",
            color: ["linkedin_url", "email", "profile_image"].includes(key) ? "#9C4FFF" : "#344054",
            fontSize: 14,
            lineHeight: "24px",
            overflowWrap: "anywhere",
            py: 1,
          }}
        >
          {key === "company"
            ? companyName
            : text(contact[key] || (key === "role" ? contact.title : ""), "-")}
        </Box>
      )}
    </Box>
  );
  const details = (
    <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
      <SectionHeader
        title="Contact Information"
        editing={editing}
        saving={saving}
        onEdit={() => setEditing(true)}
        onCancel={() => {
          setEditing(false);
          setSaveError("");
          setForm({
            name: contact.name || "",
            role: contact.role || contact.title || "",
            email: contact.email || "",
            phone: contact.phone || "",
            linkedin_url: contact.linkedin_url || "",
            source: contact.source || "",
            location: contact.location || "",
            owner: contact.owner || "",
            profile_image: contact.profile_image || "",
            contact_kind: contact.contact_kind || "contact",
          });
        }}
        onSave={save}
      />
      {saveError ? (
        <Typography
          sx={{
            color:
              saveError === "Contact information saved."
                ? "#027A48"
                : "#B42318",
            mt: 1.5,
            fontSize: 12,
          }}
        >
          {saveError}
        </Typography>
      ) : null}
      <Box
        sx={{
          pt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        {field("Name", "name")}
        {field("Title", "role")}
        {field("Company", "company", true)}
        {field("Email", "email")}
        {field("Phone", "phone")}
        {field("LinkedIn", "linkedin_url")}
        {field("Location", "location")}
        {field("Source", "source")}
        {field("Owner", "owner")}
        {field("Profile Image", "profile_image")}
        {field("Contact Type", "contact_kind")}
      </Box>
    </Box>
  );
  const relationship = () => {
    const [heading, columns] = TAB_COPY[tab];
    if (tab === "proposals") return <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}><SectionHeader title="Proposals" /><Box sx={{ mt: 2 }}>{proposalRows.length ? <GenericDataTable columns={proposalColumns} rows={proposalRows} renderRow={renderProposalRow} defaultOrderBy="name" includeSelectionColumn={false} initialRowsPerPage={10} showResultsHeader={false} /> : <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>No proposals have been sent to this contact.</Typography>}</Box></Box>;
    if (tab === "notes") return <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}><JobNotesCard apiBase={API_BASE} entityId={id} entityType="contact" activities={contactActivities} notesText={notesText} onRefresh={(activity) => {
      if (activity) {
        setContactActivities((previous) => [
          activity,
          ...previous.filter((item) => item.id !== activity.id),
        ]);
      }
      setActivitiesRefreshKey((previous) => previous + 1);
    }} onOpenAllNotes={() => setTab("activities")} /></Box>;
    if (tab === "files") return <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}><ContactDocuments files={contactFiles} uploading={uploadingFile} deleting={deletingFiles} error={fileError} selectedIds={selectedFileIds} canUpload={Boolean(id)} onUpload={uploadContactFile} onDelete={deleteSelectedFiles} onSelectedIdsChange={setSelectedFileIds} /></Box>;
    if (tab === "sequences")
      return (
        <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
          <SectionHeader title="Sequences" />
          <Box sx={{ mt: 2 }}>
            {sequencesLoading ? (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                Loading sequences...
              </Typography>
            ) : relatedSequences.length ? (
              <GenericDataTable
                columns={sequenceColumns}
                rows={relatedSequences}
                renderRow={renderSequenceRow}
                defaultOrderBy="name"
                includeSelectionColumn={false}
                initialRowsPerPage={10}
                showResultsHeader={false}
              />
            ) : (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                This contact is not part of any sequences yet.
              </Typography>
            )}
          </Box>
        </Box>
      );
    if (tab === "leads")
      return (
        <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
          <SectionHeader title="Leads" />
          <Box sx={{ mt: 2 }}>
            {leadsLoading ? (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                Loading related leads...
              </Typography>
            ) : relatedLeads.length ? (
              <GenericDataTable
                columns={leadColumns}
                rows={relatedLeads}
                renderRow={renderLeadRow}
                defaultOrderBy="company_name"
                includeSelectionColumn={false}
                initialRowsPerPage={10}
                showResultsHeader={false}
              />
            ) : (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                No business opportunities are associated with this contact.
              </Typography>
            )}
          </Box>
        </Box>
      );
    if (tab === "jobs")
      return (
        <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
          <SectionHeader title="Jobs" />
          <Box sx={{ mt: 2 }}>
            {jobsLoading ? (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                Loading related jobs...
              </Typography>
            ) : relatedJobs.length ? (
              <GenericDataTable
                columns={jobColumns}
                rows={relatedJobs}
                renderRow={renderJobRow}
                defaultOrderBy="job_title"
                includeSelectionColumn={false}
                initialRowsPerPage={10}
                showResultsHeader={false}
              />
            ) : (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                No jobs are associated with this contact.
              </Typography>
            )}
          </Box>
        </Box>
      );
    if (tab === "candidates")
      return (
        <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
          <SectionHeader title="Candidates" />
          <Box sx={{ mt: 2 }}>
            {candidatesLoading ? (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                Loading related candidates...
              </Typography>
            ) : relatedCandidates.length ? (
              <GenericDataTable
                columns={candidateColumns}
                rows={relatedCandidates}
                renderRow={renderCandidateRow}
                defaultOrderBy="full_name"
                includeSelectionColumn={false}
                initialRowsPerPage={10}
                showResultsHeader={false}
              />
            ) : (
              <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
                No candidates have been presented to or reviewed by this
                contact.
              </Typography>
            )}
          </Box>
        </Box>
      );
    if (tab === "activities")
      return activitiesLoading ? (
        <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}><Typography sx={{ color: "#98A2B3", fontSize: 14 }}>Loading activities...</Typography></Box>
      ) : (
        <LeadActivitiesTabContent activities={contactActivities} entityType="contact" entityId={id} readOnly />
      );
    return (
      <Box sx={TAB_VIEW_CONTENT_WRAPPER_SX}>
        <SectionHeader title={heading} />
        <Box sx={{ overflowX: "auto", mt: 2 }}>
          <Box
            sx={{
              minWidth: 600,
              display: "grid",
              gridTemplateColumns: `repeat(${columns.length}, minmax(130px, 1fr))`,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #D9E5F3",
              px: 1.25,
              py: 1.1,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column}
                sx={{ color: "#667085", fontSize: 11, fontWeight: 600 }}
              >
                {column}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              minWidth: 600,
              px: 1.25,
              py: 3,
              textAlign: "center",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography sx={{ color: "#98A2B3", fontSize: 12.5 }}>
              No {heading.toLowerCase()} linked to this contact yet.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  return (
    <>
    <ListTableTabsTemplate
      rootSx={{
        // Keep contact names, company information, descriptions, and table values
        // consistently readable across every profile tab.
        "& .MuiTableBody-root .MuiTypography-root": {
          fontSize: "14px !important",
        },
        "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
          fontSize: "14px",
        },
        "& .widget .MuiTypography-root:not(.heading)": {
          fontSize: "14px",
        },
        "& .MuiButtonBase-root:focus, & .MuiButtonBase-root.Mui-focusVisible": {
          outline: "none",
          boxShadow: "none",
        },
        "& .MuiButtonBase-root:focus-visible": {
          outline: "none",
        },
      }}
      breadcrumb={
        <Breadcrumb
          items={[
            { label: "Overview", onClick: () => navigate("/overview") },
            { label: "Companies", onClick: () => navigate("/companies") },
            { label: companyName, onClick: () => navigate(companyUrl) },
            { label: name },
          ]}
        />
      }
      statsSection={
        <StatsSlider
          items={statItems}
          autoPlay={false}
          showArrows
          cardMinWidth={150}
          gap={12}
        />
      }
      headerSection={
        <CompanyProfileHeader
          company={contact}
          title="Contact Profile"
          tab={tab}
          onTabChange={setTab}
          tabs={CONTACT_TABS.map((item) => ({
            ...item,
            count: item.value === "details" ? undefined : counts[item.value],
          }))}
          showAction
          actionLabel="Status"
          newAction={contactStatus}
          onNewActionChange={saveStatus}
          actionFeedback={statusFeedback}
          onActionFeedbackClose={() =>
            setStatusFeedback((previous) => ({ ...previous, open: false }))
          }
          actionShowPlaceholderOption={false}
          actionOptions={[
            { value: "New", label: "New" },
            { value: "Contacted", label: "Contacted" },
            { value: "Discovery", label: "Discovery" },
            { value: "Proposal", label: "Proposal" },
            { value: "Client", label: "Client" },
            { value: "Do Not Contact", label: "Do Not Contact" },
          ]}
          metaLabel="Company"
          metaValue={companyName}
          onMetaValueClick={linkedCompanyId ? () => navigate(companyUrl) : undefined}
          dateText={
            contact.updated_at
              ? new Date(contact.updated_at).toLocaleDateString()
              : ""
          }
          summaryRow={summaryRow}
          sx={TAB_VIEW_HEADER_SX}
        />
      }
      contentSection={tab === "details" ? details : relationship()}
      rightSidebar={
        <>
          <ClientPortalAccess key={id} contactId={id} />
          <QuickActionsCard
            actionItems={quickActionItems}
            titleSx={{ mb: 0 }}
            actionsContainerSx={{ gap: 1.25 }}
            buttonSx={{
              height: 40,
              px: 1.75,
              fontSize: 12.5,
              fontWeight: 500,
              boxShadow: "none",
              "& .MuiButton-startIcon": { mr: 0.75, ml: 0 },
            }}
            sx={{ p: 4, gap: 3 }}
          />
          <AIAgentCard
            entityType="contact"
            entityId={contact.id || id}
            entityName={contact.name || contact.full_name}
            companySeed={{
              name: companyName,
              website:
                contact.company_website ||
                contact.website ||
                contact.entity_website ||
                "",
              notes: `Create or refresh ${companyName}, scan the public website for company information, leadership, team members, and current openings.`,
            }}
            currentCompanyId={linkedCompanyId}
            currentCompanyName={companyName}
          />
          {contactSidebarWidgets}
        </>
      }
      rightWidth={300}
      shellSx={{ pt: 0 }}
    />
      <AddToSequenceDialog
        open={isAddToSequenceOpen}
        onClose={() => setIsAddToSequenceOpen(false)}
        entityType={sequenceItem.entityType}
        items={[sequenceItem]}
        onCreated={() => {
          setIsAddToSequenceOpen(false);
          setActivitiesRefreshKey((previous) => previous + 1);
        }}
      />
    </>
  );
}
